import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuillModule } from 'ngx-quill';
import { Router } from '@angular/router';
import { FeedbackService } from 'src/app/services/feedback.service';
import { FeedbackRequest, FeedbackType } from 'src/app/types/feedback/feedback-request';
import { ProfileService } from 'src/app/core/services/profile/profile.service';
import { Profile } from 'src/app/core/services/profile/profile';
import { PanelContextStore } from '@stores';
import { environment } from 'src/environments/environment';

export interface FeedbackDialogData {
  type?: FeedbackType;
  description?: string;
}

@Component({
  selector: 'feedback-dialog',
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatProgressSpinnerModule,
    QuillModule
  ]
})
export class FeedbackDialogComponent {
  private readonly data = inject<FeedbackDialogData | undefined>(MAT_DIALOG_DATA, { optional: true });
  private readonly dialogRef = inject(MatDialogRef<FeedbackDialogComponent>);
  private readonly snackBar = inject(MatSnackBar);
  private readonly feedbackService = inject(FeedbackService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly panelContextStore = inject(PanelContextStore);

  private profile!: Profile;

  readonly isSubmitting = signal(false);

  readonly quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link'],
      ['clean']
    ]
  };

  feedbackForm = this.formBuilder.group({
    type: [(this.data?.type ?? 'Feedback') as FeedbackType, Validators.required],
    title: ['', Validators.required],
    description: [this.data?.description ?? '', Validators.required],
    // Only applies to Error reports (see template) — unchecked by default, since
    // most errors are expected to just be logged rather than paging the team.
    notifyTeam: [false]
  });

  constructor(profileService: ProfileService) {
    profileService.getProfile().subscribe(p => (this.profile = p));
  }

  onSubmit(): void {
    if (this.isSubmitting() || !this.feedbackForm.valid) return;

    this.isSubmitting.set(true);
    const { type, title, description, notifyTeam } = this.feedbackForm.getRawValue();

    const request: FeedbackRequest = {
      type: type as FeedbackType,
      title: title!,
      description: description!,
      route: this.router.url,
      contractId: this.panelContextStore.contractId(),
      tradeId: this.panelContextStore.tradeId(),
      submittedByUserId: this.profile.id!,
      userAgent: navigator.userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      environment: environment.environmentName,
      // Only Errors can opt in to notifying the team; the checkbox stays hidden
      // (and false) for Feedback/Feature Request.
      notifyTeam: type === 'Error' && !!notifyTeam
    };

    this.feedbackService.submit(request).subscribe({
      next: () => {
        this.snackBar.open('Thanks for your feedback!', '', { duration: 3000 });
        this.dialogRef.close();
      },
      error: () => {
        this.snackBar.open('Failed to submit feedback. Please try again.', '', { duration: 6000 });
        this.isSubmitting.set(false);
      }
    });
  }
}
